import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SettingOutlined, GlobalOutlined, ShopOutlined, DeploymentUnitOutlined, CodeOutlined, BankOutlined } from '@ant-design/icons';
import { SbTooltip, SbRow, SbCol } from 'uiCore';
import useAuthStore from 'Store/useAuthStore';
import SetupCard from './SetupCard';
import { getStatusBadge } from './cardHelpers';
import BrandingModal from './modals/BrandingModal';
import IndustryModal from './modals/IndustryModal';
import CompanyContextModal from './modals/CompanyContextModal';
import {
  SectionWrapperStyled,
  SectionHeaderStyled,
  SectionTitleStyled,
} from '../AdminSetup.styles';

// PRD §5.16 — tooltip content strings for the Admin Setup Organization cards
const TOOLTIP_BRANDING = "Configure your organisation's branding — logo, colours, and fonts used in generated documents.";
const TOOLTIP_INDUSTRY = "Set your organisation's industry classification. Used to benchmark roles and calibrate AI-generated content.";
const TOOLTIP_COMPANY_CONTEXT = "Add high-level context about your organisation — size, mission, and strategic focus. Used to personalise all AI-generated JDs.";
const TOOLTIP_TECH_CONTEXT = "List the tools, systems, and technologies your organisation uses. Referenced during JD and PAIC generation.";
const TOOLTIP_LEGAL_ENTITY = "Manage your legal entities — business units, functions & sub-functions.";

const OrganizationSection = ({ cards = {}, onUpdate, loading = false }) => {
  const navigate = useNavigate();
  const organizationStructure = useAuthStore(
    (s) => s.orgMetaData?.organization_structure,
  );
  const legalEntitiesCount = (organizationStructure || []).filter(
    (e) => e?.legal_entity,
  ).length;
  const [modalVisible, setModalVisible] = useState({
    branding: false,
    industry: false,
    company_context: false,
  });

  const handleCardClick = (cardKey, route) => {
    if (loading) return; // Prevent clicks during loading
    
    // If it's a navigation card, navigate to route
    if (route) {
      navigate(route);
      return;
    }
    
    // Otherwise open modal
    setModalVisible((prev) => ({ ...prev, [cardKey]: true }));
  };

  const handleCloseModal = (cardKey) => {
    setModalVisible((prev) => ({ ...prev, [cardKey]: false }));
  };

  const cardConfig = [
    {
      key: 'branding',
      title: 'Branding',
      data: cards.branding,
      Modal: BrandingModal,
      icon: <GlobalOutlined />,
      tooltip: TOOLTIP_BRANDING,
    },
    {
      key: 'legal_entity',
      title: 'Legal Entity',
      data: {
        status: legalEntitiesCount ? 'completed' : 'empty',
        helper_text: '4 legal entities',
      },
      route: '/admin-setup/legal-entity',
      icon: <BankOutlined />,
      tooltip: TOOLTIP_LEGAL_ENTITY,
    },
    {
      key: 'industry',
      title: 'Industry',
      data: cards.industry,
      Modal: IndustryModal,
      icon: <ShopOutlined />,
      tooltip: TOOLTIP_INDUSTRY,
    },
    {
      key: 'company_context',
      title: 'Company Context',
      data: cards.company_context,
      Modal: CompanyContextModal,
      icon: <DeploymentUnitOutlined />,
      tooltip: TOOLTIP_COMPANY_CONTEXT,
    },
    {
      key: 'org_tech_context',
      title: 'Technology Context',
      data: {
        status: 'completed',
        helper_text: 'Catalogue tools & tech stack for technical JDs and PAIC'
      },
      route: '/admin-setup/org-tech-context-setup',
      icon: <CodeOutlined />,
      tooltip: TOOLTIP_TECH_CONTEXT,
    },
  ];

  return (
    <SectionWrapperStyled>
      <SectionHeaderStyled align="center">
        <SettingOutlined />
        <SectionTitleStyled>ORGANIZATION MANAGEMENT</SectionTitleStyled>
      </SectionHeaderStyled>
      <SbRow gutter={[14, 14]}>
        {cardConfig.map(({ key, title, data, Modal, icon, route, tooltip }) => (
          <React.Fragment key={key}>
            <SbCol xs={24} sm={12} md={8} lg={24 / 5} xl={4}>
              <SbTooltip content={tooltip}>
                <div>
                  <SetupCard
                    icon={icon}
                    title={title}
                    description={data?.helper_text}
                    badge={getStatusBadge(data?.status || 'empty')}
                    onClick={() => handleCardClick(key, route)}
                  />
                </div>
              </SbTooltip>
            </SbCol>

            {!loading && Modal && (
              <Modal
                visible={modalVisible[key]}
                onClose={() => handleCloseModal(key)}
                initialData={data?.data || {}}
                cardKey={key}
                onSave={onUpdate}
              />
            )}
          </React.Fragment>
        ))}
      </SbRow>
    </SectionWrapperStyled>
  );
};

export default OrganizationSection;
